import Box from "@mui/material/Box";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel, { stepLabelClasses } from "@mui/material/StepLabel";
import {
  Stack,
  StepConnector,
  StepIconProps,
  styled,
  useTheme,
} from "@mui/material";
import { ActiveStagesDto } from "@/modules/property-track-settings/types";
import { useMemo } from "react";
import { Check } from "lucide-react";

const StyledStack = styled(Stack, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "completed",
})<{ active?: boolean; completed?: boolean }>(
  ({ active, completed, theme }) => ({
    alignItems: "center",
    justifyContent: "center",
    backgroundColor:
      active || completed
        ? theme.nawy.colors.primitives.green[100]
        : "transparent",
    border: "2px solid",
    borderColor:
      active || completed
        ? "transparent"
        : theme.nawy.colors.semantic.border.primary,
    borderRadius: "4px",
    width: 20,
    height: 20,
  })
);

function StageIcon(props: Readonly<StepIconProps>) {
  const { completed, active } = props;
  const theme = useTheme();
  if (active || completed)
    return (
      <StyledStack active={active} completed={completed}>
        <Check
          color={theme.nawy.colors.primitives.green[800]}
          strokeWidth={3}
          size={14}
        />
      </StyledStack>
    );
  return <StyledStack />;
}

const StyledStepLabel = styled(StepLabel, {
  shouldForwardProp: (prop) => prop !== "completed" && prop !== "active",
})<{ completed?: boolean; active?: boolean }>(
  ({ completed, active, theme }) => ({
    [`&.${stepLabelClasses.root}`]: {
      flexDirection: "column",
      alignItems: "center",
    },
    [`& .${stepLabelClasses.iconContainer}`]: {
      padding: 0,
    },
    [`& .${stepLabelClasses.labelContainer}`]: {
      width: "max-content",
      paddingTop: "4px",
    },
    [`& .${stepLabelClasses.label}`]: {
      ...theme.nawy.typography.english.variants.body.sm.regular,
      color:
        completed || active
          ? theme.nawy.colors.primitives.green[800]
          : theme.nawy.colors.semantic.text.secondary,
    },
  })
);

export default function StagesStepper({
  trackStages,
  activeStage = 0,
  stagesPrecedencesHistory,
}: Readonly<{
  trackStages: ActiveStagesDto[] | undefined;
  activeStage: number | undefined;
  stagesPrecedencesHistory: number[] | undefined;
}>) {
  const orderedStages = useMemo(() => {
    if (!trackStages) return [];

    return [...trackStages].sort((a, b) => a?.precedence - b?.precedence);
  }, [trackStages]);

  const isStepCompleted = (stage: ActiveStagesDto) => {
    const isStepSameAsActiveStage = stage.precedence === activeStage;

    return (
      stagesPrecedencesHistory?.includes(stage.precedence) &&
      !isStepSameAsActiveStage &&
      stage.precedence < activeStage
    );
  };

  return (
    <Box sx={{ width: "100%", overflow: "auto", scrollbarWidth: "none" }}>
      <Stepper
        activeStep={activeStage - 1}
        // alternativeLabel
        connector={
          <StepConnector sx={{ mx: "2vw", mb: 2.5, minWidth: "31px" }} />
        }
      >
        {orderedStages &&
          orderedStages.map((stage) => (
            <Step key={stage.id} completed={isStepCompleted(stage)}>
              <StyledStepLabel
                active={stage.precedence === activeStage}
                completed={isStepCompleted(stage)}
                slots={{ stepIcon: StageIcon }}
              >
                {stage.name}
              </StyledStepLabel>
            </Step>
          ))}
      </Stepper>
    </Box>
  );
}
